import React, { useEffect, useState } from "react";
import { AddUserForm } from "../AddUser/AddUser";
import { SelectOption } from "../AddUser/AddUser.types";
import AlertMessage from "../AlertMessage/AlertMessage";
import Loader from "../Loader/Loader";
import {
  GetRbacConfigService,
  GetAllClientsListService,
  GetUserByIdService,
  EditUserService,
  EditUserPayload,
} from "../../Services/UserManagementService/UserManagementService";
import { UserDetails } from "../../Services/UserManagementService/userManagement.mapper";

interface EditUserProps {
  visible: boolean;
  onClose: () => void;
  rowData?: any;
  // CustomTable passes its own fetchData through as this, so the table
  // refreshes after a successful edit.
  data?: () => void;
}

const EditUser: React.FC<EditUserProps> = ({
  visible,
  onClose,
  rowData,
  data,
}) => {
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  // The table row only carries a display-friendly subset of the user (no
  // assigned client ids) — this holds the full record fetched by id, which
  // is what actually fills the form.
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingOption, setLoadingOption] = useState(false);

  // Also used as the refresh handler for the button next to the client
  // search box (e.g. after creating a new client).
const fetchClientsList = async () => {
 
  setLoadingOption(true);

  try {
    const clients = await GetAllClientsListService();

    setClientOptions(
      clients.map((client) => ({
        label: client.name,
        value: client.id,
      }))
    );
  } catch (error) {
    console.error("Error fetching clients list:", error);
  } finally {
    setLoadingOption(false);
  }
};

  // Fetches its own role/client options rather than relying on CustomTable
  // to pass them through — CustomTable is shared by many pages, this way it
  // doesn't need to know anything about RBAC or clients.
  useEffect(() => {
    if (!visible) return;

    const fetchRbacConfig = async () => {
      const rbacConfig = await GetRbacConfigService();
      setRoleOptions(
        rbacConfig?.assignableRoles?.map((role: any) => ({
          label: role.name,
          value: role.name,
        })) ?? [],
      );
    };

    const fetchUserDetails = async () => {
      const userId = rowData?.userId ?? rowData?.id;
      if (!userId) return;

      
      setUserDetails(await GetUserByIdService(userId));
     
    };

    fetchRbacConfig();
    fetchClientsList();
    fetchUserDetails();
  }, [visible, rowData]);

  if (!visible) return null;

  // Formik only reads initialValues once, at mount, so the form must not
  // render until the real user details are in — otherwise it would mount
  // with blank/stale values and never pick up the fetched ones.
  if (loadingUser || !userDetails) {
    return <Loader />;
  }

  const initialValues = {
    firstName: userDetails.firstName,
    lastName: userDetails.lastName,
    email: userDetails.email,
    role: userDetails.role,
    client: userDetails.client,
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const payload: EditUserPayload = {
      userId: userDetails.userId,
      ...values,
    };

    const result = await EditUserService(payload);

    if (result.success) {
      AlertMessage({ type: "success", text: result.message });
      onClose();
      data?.();
    } else {
      AlertMessage({ type: "error", text: result.message });
    }
  };

  return (
    <AddUserForm
      visible={visible}
      onClose={onClose}
      heading="Edit User"
      submitLabel="Update"
      roleOptions={roleOptions}
      clientOptions={clientOptions}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onClientsRefresh={fetchClientsList}
      isedit={true}
      loadingOption={loadingOption}
    />
  );
};

export default EditUser;
